import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  Pressable,
  StyleSheet,
  ScrollView,
  Platform,
  Modal,
  TextInput,
  Dimensions,
  Linking,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import MaterialIcons from '@expo/vector-icons/MaterialIcons';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import YoutubeOAuthBar from '../components/YoutubeOAuthBar';
import { getApiUrl } from '../utils/apiConfig';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import {
  uploadYoutubeBinaryFromBase64,
  formatYoutubeUploadError,
  isYoutubeReconnectError,
} from '../utils/youtubeBinaryUpload';
import {
  assertVideoUploadable,
  getYoutubeVideoPickerOptions,
} from '../utils/videoUploadLimits';
import UserBreakVideosModal from '../components/UserBreakVideosModal';
import {
  normalizeUserBreakVideoLinks,
  hasUserBreakVideos,
} from '../utils/userBreakVideoLinks';
import {
  fetchExistingUserBreakMeta,
  resolveBreakId,
  saveUserBreakWithVideo,
} from '../utils/userBreakUpload';

const { width } = Dimensions.get('window');

const TABS = [
  { id: 'course', label: 'Course breaks' },
  { id: 'grade', label: 'Grade breaks' },
];

const UPLOAD_MODAL_TABS = [
  { id: 'url', label: 'URL Upload' },
  { id: 'file', label: 'File Upload' },
];

const BREAKS_TABLE_MIN_WIDTH = 800;
const COMPLETED_CAMERA_COLOR = '#2e7d32';
const BREAKS_TABLE_COLUMNS = [
  { key: 'break_name', label: 'Break', flex: 1.22 },
  { key: 'completed', label: 'Completed', flex: 0.62 },
  { key: 'weapon', label: 'Weapon', flex: 0.72 },
  { key: 'side', label: 'Side', flex: 0.55 },
  { key: 'material_type', label: 'Material', flex: 0.78 },
  { key: 'quantity', label: 'Qty', flex: 0.38 },
  { key: 'size_inches', label: 'Size (in)', flex: 0.48 },
  { key: 'total_size_inches', label: 'Total (in)', flex: 0.52 },
];

function normalizeBreakRow(row) {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: row.break_id ?? row.id,
    video_link: normalizeUserBreakVideoLinks(row.video_link),
    user_break_id: row.user_break_id ?? null,
  };
}

function formatBreakBuilderCell(breakItem, key) {
  if (key === 'completed') return '';
  const v = breakItem?.[key];
  if (v == null || v === '') return '—';
  if (key === 'size_inches') {
    const n = Number(v);
    if (Number.isFinite(n)) return String(Math.round(n));
  }
  return String(v);
}

function BreaksBuilderFilterDropdown({
  styles,
  label,
  selectedValue,
  options,
  open,
  onOpenChange,
  onSelect,
}) {
  const triggerRef = useRef(null);
  const [menuRect, setMenuRect] = useState(null);
  const displayValue =
    selectedValue != null && selectedValue !== '' ? `(${selectedValue})` : '';

  const close = useCallback(() => {
    onOpenChange(false);
    setMenuRect(null);
  }, [onOpenChange]);

  const toggle = useCallback(() => {
    if (open) {
      close();
      return;
    }
    if (Platform.OS === 'web' && triggerRef.current?.measureInWindow) {
      triggerRef.current.measureInWindow((x, y, width, height) => {
        setMenuRect({
          top: y + height + 4,
          left: x,
          width: Math.max(width, 160),
        });
        onOpenChange(true);
      });
      return;
    }
    onOpenChange(true);
  }, [open, close, onOpenChange]);

  const webMenuReady = Platform.OS !== 'web' || menuRect != null;

  const menuNode =
    open && webMenuReady ? (
      <>
        {Platform.OS === 'web' ? (
          <TouchableOpacity
            style={styles.filterDropdownOverlay}
            activeOpacity={1}
            onPress={close}
            {...(Platform.OS === 'web'
              ? { dataSet: { breaksBuilderFilterOverlay: 'true' } }
              : {})}
          />
        ) : null}
        <View
          style={[
            styles.filterDropdown,
            Platform.OS === 'web' && menuRect
              ? {
                  position: 'fixed',
                  top: menuRect.top,
                  left: menuRect.left,
                  width: menuRect.width,
                  right: undefined,
                }
              : null,
          ]}
          {...(Platform.OS === 'web'
            ? { dataSet: { breaksBuilderFilterMenu: 'true' } }
            : {})}
        >
          <ScrollView style={styles.filterDropdownScroll} nestedScrollEnabled>
            <TouchableOpacity
              style={styles.filterOption}
              onPress={() => {
                onSelect(null);
                close();
              }}
              {...(Platform.OS === 'web'
                ? { dataSet: { breaksBuilderFilterOption: 'true' } }
                : {})}
            >
              <Text
                style={styles.filterOptionText}
                {...(Platform.OS === 'web'
                  ? { dataSet: { breaksBuilderFilterOptionText: 'true' } }
                  : {})}
              >
                All
              </Text>
            </TouchableOpacity>
            {options.map((option) => (
              <TouchableOpacity
                key={String(option)}
                style={[
                  styles.filterOption,
                  selectedValue === option && styles.filterOptionSelected,
                ]}
                onPress={() => {
                  onSelect(option);
                  close();
                }}
                {...(Platform.OS === 'web'
                  ? { dataSet: { breaksBuilderFilterOption: 'true' } }
                  : {})}
              >
                <Text
                  style={styles.filterOptionText}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { breaksBuilderFilterOptionText: 'true' } }
                    : {})}
                >
                  {option}
                </Text>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </>
    ) : null;

  const portaledMenu =
    Platform.OS === 'web' && menuNode && typeof document !== 'undefined'
      ? require('react-dom').createPortal(
          <div
            data-breaks-builder-filter-portal="true"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 100000,
              pointerEvents: 'none',
            }}
          >
            <div style={{ pointerEvents: 'auto', width: '100%', height: '100%' }}>
              {menuNode}
            </div>
          </div>,
          document.body
        )
      : null;

  return (
    <>
      <View
        style={styles.filterContainer}
        {...(Platform.OS === 'web'
          ? { dataSet: { breaksBuilderFilter: 'true' } }
          : {})}
      >
        <View ref={triggerRef} collapsable={false}>
          <TouchableOpacity
            style={[
              styles.filterButton,
              selectedValue != null &&
                selectedValue !== '' &&
                styles.filterButtonActive,
            ]}
            onPress={toggle}
          >
            <Text
              style={styles.filterButtonText}
              {...(Platform.OS === 'web'
                ? { dataSet: { breaksBuilderFilterText: 'true' } }
                : {})}
            >
              {label} {displayValue}
            </Text>
            <Text style={styles.dropdownArrow}>{open ? '▲' : '▼'}</Text>
          </TouchableOpacity>
        </View>
        {Platform.OS !== 'web' ? menuNode : null}
      </View>
      {portaledMenu}
    </>
  );
}

function BreaksBuilderTableHeader({ styles, isDarkMode, tableInnerStyle }) {
  const headerBg = isDarkMode ? styles.tableHeaderRowBgDark : styles.tableHeaderRowBgLight;
  const headerTextStyle = isDarkMode ? styles.tableHeaderTextDark : styles.tableHeaderTextLight;
  return (
    <View style={[styles.tableHeaderRow, tableInnerStyle, headerBg]}>
      {BREAKS_TABLE_COLUMNS.map((col) => (
        <Text
          key={col.key}
          style={[styles.tableHeaderCell, { flex: col.flex }, headerTextStyle]}
          numberOfLines={2}
        >
          {col.label}
        </Text>
      ))}
    </View>
  );
}

function BreaksBuilderTableRow({
  breakItem,
  alt,
  onPressRow,
  onPressCompletedVideo,
  styles,
  isDarkMode,
  tableInnerStyle,
}) {
  const rowBg = alt ? styles.tableRowAlt : styles.tableRowBase;
  const textStyle = isDarkMode ? styles.tableCellTextDark : styles.tableCellTextLight;
  const showCompleted =
    breakItem?.user_break_id != null && hasUserBreakVideos(breakItem?.video_link);
  const rowDataSet =
    Platform.OS === 'web' ? { dataSet: { breaksBuilderRow: alt ? 'alt' : 'base' } } : {};
  return (
    <Pressable
      style={({ pressed }) => [
        styles.tableDataRow,
        tableInnerStyle,
        rowBg,
        pressed && styles.tableRowPressed,
      ]}
      onPress={() => onPressRow(breakItem)}
      {...rowDataSet}
    >
      {BREAKS_TABLE_COLUMNS.map((col) => {
        if (col.key === 'completed') {
          return (
            <View
              key={col.key}
              style={[styles.tableCellWrap, styles.completedCell, { flex: col.flex }]}
              {...(Platform.OS === 'web'
                ? { dataSet: { breaksBuilderCompletedCell: 'true' } }
                : {})}
            >
              {showCompleted ? (
                <TouchableOpacity
                  style={styles.completedCellButton}
                  onPress={(e) => {
                    if (Platform.OS === 'web') {
                      e?.stopPropagation?.();
                      e?.preventDefault?.();
                    }
                    onPressCompletedVideo(breakItem);
                  }}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  accessibilityRole="button"
                  accessibilityLabel={`Play completed video for ${breakItem.break_name}`}
                  {...(Platform.OS === 'web'
                    ? { dataSet: { breaksBuilderCompletedCell: 'true' } }
                    : {})}
                >
                  <MaterialIcons
                    name="videocam"
                    size={26}
                    color={COMPLETED_CAMERA_COLOR}
                  />
                </TouchableOpacity>
              ) : null}
            </View>
          );
        }
        return (
          <Text
            key={col.key}
            style={[styles.tableCellText, { flex: col.flex }, textStyle]}
            numberOfLines={col.key === 'break_name' ? 4 : 2}
          >
            {formatBreakBuilderCell(breakItem, col.key)}
          </Text>
        );
      })}
    </Pressable>
  );
}

function BreaksBuilderTable({
  breaks,
  styles,
  isDarkMode,
  onPressRow,
  onPressCompletedVideo,
}) {
  const tableInnerStyle = {
    minWidth: BREAKS_TABLE_MIN_WIDTH,
    width: BREAKS_TABLE_MIN_WIDTH,
  };
  return (
    <View style={styles.syllabusTableCard}>
      <ScrollView
        horizontal
        nestedScrollEnabled
        showsHorizontalScrollIndicator={Platform.OS === 'web'}
        style={styles.tableHorizontalScroll}
        contentContainerStyle={styles.tableHorizontalScrollContent}
      >
        <View style={styles.tableInner}>
          <BreaksBuilderTableHeader
            styles={styles}
            isDarkMode={isDarkMode}
            tableInnerStyle={tableInnerStyle}
          />
          {breaks.map((breakItem, index) => (
            <BreaksBuilderTableRow
              key={breakItem.id}
              breakItem={breakItem}
              alt={index % 2 === 1}
              onPressRow={onPressRow}
              onPressCompletedVideo={onPressCompletedVideo}
              styles={styles}
              isDarkMode={isDarkMode}
              tableInnerStyle={tableInnerStyle}
            />
          ))}
        </View>
      </ScrollView>
    </View>
  );
}

export default function BreaksBuilder({ navigation, route }) {
  const theme = useTheme();
  const currentUser = route?.params?.user || route?.params || null;
  const stopwatchRef = useRef(null);
  const [activeTab, setActiveTab] = useState('course');
  
  // Course tab state
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [showCourseDropdown, setShowCourseDropdown] = useState(false);
  const [loadingCourses, setLoadingCourses] = useState(false);
  
  // Student state
  const [students, setStudents] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showStudentDropdown, setShowStudentDropdown] = useState(false);
  const [loadingStudents, setLoadingStudents] = useState(false);
  
  // Filter state
  const [selectedWeapon, setSelectedWeapon] = useState(null);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [selectedQuantity, setSelectedQuantity] = useState(null);
  const [availableWeapons, setAvailableWeapons] = useState([]);
  const [availableMaterials, setAvailableMaterials] = useState([]);
  const [availableQuantities, setAvailableQuantities] = useState([]);
  const [showWeaponDropdown, setShowWeaponDropdown] = useState(false);
  const [showMaterialDropdown, setShowMaterialDropdown] = useState(false);
  const [showQuantityDropdown, setShowQuantityDropdown] = useState(false);
  
  // Breaks state
  const [breaks, setBreaks] = useState([]);
  const [loadingBreaks, setLoadingBreaks] = useState(false);
  const [completedVideoBreak, setCompletedVideoBreak] = useState(null);
  
  // Modal state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedBreakForUpload, setSelectedBreakForUpload] = useState(null);
  const [uploadModalTab, setUploadModalTab] = useState('url');
  const [uploadUrl, setUploadUrl] = useState('');
  const [pickedFileName, setPickedFileName] = useState('');
  const [uploadModalError, setUploadModalError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showUserBreakSuccess, setShowUserBreakSuccess] = useState(false);
  const [showYouTubeSuccess, setShowYouTubeSuccess] = useState(false);
  const [youtubeUrl, setYoutubeUrl] = useState(null);
  const pickedBase64Ref = useRef(null);
  const webFileInputRef = useRef(null);
  const pickingFileRef = useRef(false);
  const youtubeOAuthRef = useRef(null);
  
  // Grade tab state
  const [grades, setGrades] = useState([]);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [showGradeDropdown, setShowGradeDropdown] = useState(false);
  const [loadingGrades, setLoadingGrades] = useState(false);

  useEffect(() => {
    fetchCourses();
    fetchStudents();
    fetchGrades();
  }, []);

  useEffect(() => {
    if (activeTab === 'course' && selectedCourse) {
      fetchBreaksByCourse();
      fetchFilterOptions();
    }
  }, [
    activeTab,
    selectedCourse,
    selectedStudent,
    selectedWeapon,
    selectedMaterial,
    selectedQuantity,
  ]);

  useEffect(() => {
    if (activeTab === 'grade' && selectedGrade) {
      fetchBreaksByGrade();
      fetchFilterOptions();
    }
  }, [
    activeTab,
    selectedGrade,
    selectedStudent,
    selectedWeapon,
    selectedMaterial,
    selectedQuantity,
  ]);

  const applyBreakFilters = (rows) => {
    if (!Array.isArray(rows)) return [];
    let list = rows;
    if (selectedWeapon) {
      list = list.filter((b) => b.weapon === selectedWeapon);
    }
    if (selectedMaterial) {
      list = list.filter((b) => b.material_type === selectedMaterial);
    }
    if (selectedQuantity != null) {
      list = list.filter((b) => Number(b.quantity) === Number(selectedQuantity));
    }
    return list;
  };

  const fetchCourses = async () => {
    setLoadingCourses(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/courses`);
      if (response.ok) {
        const data = await response.json();
        setCourses(dedupeCoursesForDropdown(Array.isArray(data) ? data : []));
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    } finally {
      setLoadingCourses(false);
    }
  };

  const fetchStudents = async () => {
    setLoadingStudents(true);
    try {
      const requesterId = currentUser?.id ?? route?.params?.userId;
      const requesterQ =
        requesterId != null
          ? `&requester_id=${encodeURIComponent(String(requesterId))}`
          : '';
      const response = await fetch(`${getApiUrl()}/api/users?visible=true${requesterQ}`);
      if (response.ok) {
        const data = await response.json();
        setStudents(data);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const fetchGrades = async () => {
    setLoadingGrades(true);
    try {
      const response = await fetch(`${getApiUrl()}/api/grades`);
      if (response.ok) {
        const data = await response.json();
        setGrades(data);
      }
    } catch (error) {
      console.error('Error fetching grades:', error);
    } finally {
      setLoadingGrades(false);
    }
  };

  const fetchBreaksByCourse = async () => {
    if (!selectedCourse) return;

    setLoadingBreaks(true);
    try {
      let rows = [];
      if (selectedStudent) {
        const response = await fetch(
          `${getApiUrl()}/api/courses/${selectedCourse.id}/breaks/${selectedStudent}`
        );
        if (response.ok) {
          const data = await response.json();
          rows = Array.isArray(data) ? data.map((row) => normalizeBreakRow(row)) : [];
        }
        setBreaks(applyBreakFilters(rows));
      } else {
        let url = `${getApiUrl()}/api/breaks/course/${selectedCourse.id}`;
        const params = [];
        if (selectedWeapon) params.push(`weapon=${encodeURIComponent(selectedWeapon)}`);
        if (selectedMaterial) {
          params.push(`material_type=${encodeURIComponent(selectedMaterial)}`);
        }
        if (selectedQuantity) params.push(`quantity=${selectedQuantity}`);
        if (params.length > 0) url += `?${params.join('&')}`;

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          rows = Array.isArray(data) ? data.map((row) => normalizeBreakRow(row)) : [];
        }
        setBreaks(rows);
      }
    } catch (error) {
      console.error('Error fetching breaks by course:', error);
      setBreaks([]);
    } finally {
      setLoadingBreaks(false);
    }
  };

  const fetchBreaksByGrade = async () => {
    if (!selectedGrade) return;

    setLoadingBreaks(true);
    try {
      let rows = [];
      if (selectedStudent) {
        const response = await fetch(
          `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(selectedGrade.grade_name)}/student/${selectedStudent}`
        );
        if (response.ok) {
          const data = await response.json();
          rows = Array.isArray(data) ? data.map((row) => normalizeBreakRow(row)) : [];
        }
        setBreaks(applyBreakFilters(rows));
      } else {
        let url = `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(selectedGrade.grade_name)}`;
        const params = [];
        if (selectedWeapon) params.push(`weapon=${encodeURIComponent(selectedWeapon)}`);
        if (selectedMaterial) {
          params.push(`material_type=${encodeURIComponent(selectedMaterial)}`);
        }
        if (selectedQuantity) params.push(`quantity=${selectedQuantity}`);
        if (params.length > 0) url += `?${params.join('&')}`;

        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          rows = Array.isArray(data) ? data.map((row) => normalizeBreakRow(row)) : [];
        }
        setBreaks(rows);
      }
    } catch (error) {
      console.error('Error fetching breaks by grade:', error);
      setBreaks([]);
    } finally {
      setLoadingBreaks(false);
    }
  };

  const fetchFilterOptions = async () => {
    // Fetch unique values for filters from all breaks (not just filtered ones)
    try {
      let url = '';
      if (activeTab === 'course' && selectedCourse) {
        url = `${getApiUrl()}/api/breaks/course/${selectedCourse.id}`;
      } else if (activeTab === 'grade' && selectedGrade) {
        url = `${getApiUrl()}/api/breaks/grade/${encodeURIComponent(selectedGrade.grade_name)}`;
      } else {
        return;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const allBreaks = await response.json();
        const weapons = [...new Set(allBreaks.map(b => b.weapon).filter(Boolean))].sort();
        const materials = [...new Set(allBreaks.map(b => b.material_type).filter(Boolean))].sort();
        const quantities = [...new Set(allBreaks.map(b => b.quantity).filter(Boolean))].sort((a, b) => a - b);
        
        setAvailableWeapons(weapons);
        setAvailableMaterials(materials);
        setAvailableQuantities(quantities);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const resetUploadModalState = useCallback(() => {
    setUploadUrl('');
    setUploadModalTab('url');
    setPickedFileName('');
    pickedBase64Ref.current = null;
    setUploadModalError('');
    setShowUserBreakSuccess(false);
    setShowYouTubeSuccess(false);
    setYoutubeUrl(null);
  }, []);

  const closeUploadModal = useCallback(() => {
    if (uploading) return;
    pickingFileRef.current = false;
    setShowUploadModal(false);
    setSelectedBreakForUpload(null);
    resetUploadModalState();
  }, [uploading, resetUploadModalState]);

  useEffect(() => {
    if (!showUploadModal) return;
    void youtubeOAuthRef.current?.refresh?.();
  }, [showUploadModal]);

  const persistUserBreak = async ({
    studentId,
    breakId,
    videoLink,
    completedDate,
  }) => {
    await saveUserBreakWithVideo({
      studentId,
      breakId,
      youtubeUrl: videoLink,
      completedDate,
    });
  };

  const refreshBreaksList = () => {
    if (activeTab === 'course') {
      fetchBreaksByCourse();
    } else {
      fetchBreaksByGrade();
    }
  };

  const handleBreakClick = (breakItem) => {
    if (!selectedStudent) {
      alert('Please select a student first.');
      return;
    }
    resetUploadModalState();
    setSelectedBreakForUpload(breakItem);
    setShowUploadModal(true);
  };

  const launchFilePick = async () => {
    setUploadModalError('');
    if (Platform.OS === 'web') {
      pickingFileRef.current = true;
      if (typeof window !== 'undefined') {
        const resetPickingGuard = () => {
          window.removeEventListener('focus', resetPickingGuard);
          setTimeout(() => {
            pickingFileRef.current = false;
          }, 350);
        };
        window.addEventListener('focus', resetPickingGuard);
      }
      webFileInputRef.current?.click?.();
      return;
    }
    const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (!perm.granted) {
      setUploadModalError('Permission required to choose a video from your library.');
      return;
    }
    const result = await ImagePicker.launchImageLibraryAsync(getYoutubeVideoPickerOptions());
    if (result.canceled || !result.assets?.[0]) return;
    const a = result.assets[0];
    const name =
      a.fileName || (a.uri && String(a.uri).split('/').pop()) || 'video.mp4';
    setPickedFileName(name);
    if (a.base64) {
      const sizeMsg = assertVideoUploadable({ fileSize: a.fileSize, base64: a.base64 });
      if (sizeMsg) {
        setUploadModalError(sizeMsg);
        setPickedFileName('');
        return;
      }
      pickedBase64Ref.current = a.base64;
      return;
    }
    try {
      const response = await fetch(a.uri);
      const blob = await response.blob();
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const s = reader.result;
          if (typeof s === 'string' && s.includes(',')) resolve(s.split(',')[1]);
          else reject(new Error('Could not read file'));
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const sizeMsg = assertVideoUploadable({ fileSize: a.fileSize || blob.size, base64: b64 });
      if (sizeMsg) {
        setUploadModalError(sizeMsg);
        setPickedFileName('');
        return;
      }
      pickedBase64Ref.current = b64;
    } catch {
      setUploadModalError('Could not read the selected video.');
    }
  };

  const onWebFileChange = (e) => {
    const file = e.target?.files?.[0];
    pickingFileRef.current = false;
    if (!file) return;
    setUploadModalError('');
    setPickedFileName(file.name || 'video');
    const reader = new FileReader();
    reader.onloadend = () => {
      const s = reader.result;
      if (typeof s === 'string' && s.includes(',')) {
        pickedBase64Ref.current = s.split(',')[1];
      } else {
        setUploadModalError('Could not read the selected video.');
      }
    };
    reader.onerror = () => setUploadModalError('Could not read the selected video.');
    reader.readAsDataURL(file);
    e.target.value = '';
    setTimeout(() => {
      pickingFileRef.current = false;
    }, 400);
  };

  const openCompletedVideo = (breakItem) => {
    if (!hasUserBreakVideos(breakItem?.video_link)) return;
    setCompletedVideoBreak(breakItem);
  };

  const closeCompletedVideo = () => setCompletedVideoBreak(null);

  const isFacebookUrl = (url) => {
    if (!url) return false;
    return /facebook\.com|fb\.com/.test(url);
  };

  const formatYouTubeTitle = (breakItem, student) => {
    const studentName = student?.student_name || student?.username || 'Student';
    const side = breakItem.side || '';
    const breakName = breakItem.break_name || '';
    const quantity = breakItem.quantity || 1;
    const material = breakItem.material_type || '';
    const sizeInches = breakItem.size_inches || '';
    
    let materialStr = '';
    if (material.toLowerCase() === 'wood') {
      materialStr = sizeInches ? `${sizeInches}" wood` : 'wood';
    } else if (material.toLowerCase() === 'concrete' || material.toLowerCase() === 'concrete block') {
      if (sizeInches) {
        materialStr = `${sizeInches}" concrete block`;
      } else {
        materialStr = 'concrete block';
      }
    } else {
      materialStr = material || '';
    }
    
    // Format: "{Student} - {side} {break name} - {quantity} {material}"
    return `${studentName} - ${side} ${breakName} - ${quantity} ${materialStr}`.trim();
  };

  const handleUrlUploadSave = async () => {
    if (!selectedStudent || !selectedBreakForUpload || !uploadUrl.trim()) {
      alert('Please enter a URL');
      return;
    }

    const trimmedUrl = uploadUrl.trim();
    const sourceIsFacebook = isFacebookUrl(trimmedUrl);

    setUploading(true);
    setShowUserBreakSuccess(false);
    setShowYouTubeSuccess(false);
    setUploadModalError('');

    try {
      let finalUrl = trimmedUrl;
      let completedDate = new Date().toISOString().split('T')[0];

      const breakId = resolveBreakId(selectedBreakForUpload);
      const { titleSuffix } = await fetchExistingUserBreakMeta(
        selectedStudent,
        breakId
      );

      const student = students.find((s) => s.id === selectedStudent);
      let youtubeTitle = formatYouTubeTitle(selectedBreakForUpload, student) + titleSuffix;

      try {
        const uploadResponse = await fetch(`${getApiUrl()}/api/youtube/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceUrl: trimmedUrl,
            title: youtubeTitle,
            description: `Break: ${selectedBreakForUpload.break_name}`,
            privacyStatus: 'unlisted',
            isShort: true,
          }),
        });

        if (uploadResponse.ok) {
          const uploadData = await uploadResponse.json();
          finalUrl = uploadData.youtubeUrl || trimmedUrl;
          setShowYouTubeSuccess(true);
          setYoutubeUrl(uploadData.youtubeUrl);
          if (uploadData.uploadDate) completedDate = uploadData.uploadDate;
        } else {
          const errorData = await uploadResponse.json().catch(() => ({ error: 'Unknown error' }));
          const detail = formatYoutubeUploadError(errorData, 'Unknown error');
          if (isYoutubeReconnectError(detail)) {
            youtubeOAuthRef.current?.markNeedsReconnect?.(detail);
          }
          const setupHint =
            sourceIsFacebook && /yt-dlp/i.test(String(detail))
              ? '\n\nFacebook/Reels uploads need yt-dlp on the server. Install yt-dlp and restart the backend.'
              : '';
          const diag = errorData.diagnosticEndpoint
            ? `\nSetup check: ${getApiUrl()}${errorData.diagnosticEndpoint}`
            : '';
          alert(`YouTube upload failed:\n${detail}${setupHint}${diag}`);
          if (sourceIsFacebook) return;
        }
      } catch (error) {
        alert(`Error uploading to YouTube: ${error.message}`);
        if (sourceIsFacebook) return;
      }

      await persistUserBreak({
        studentId: selectedStudent,
        breakId,
        videoLink: finalUrl,
        completedDate,
      });

      setShowUserBreakSuccess(true);
      setTimeout(() => {
        closeUploadModal();
        refreshBreaksList();
      }, 3000);
    } catch (error) {
      console.error('Error saving break:', error);
      alert(error.message || 'An error occurred. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileUploadSave = async () => {
    if (!selectedStudent || !selectedBreakForUpload) {
      setUploadModalError('Please select a student and break.');
      return;
    }
    if (!pickedBase64Ref.current) {
      setUploadModalError('Please choose a video file with Upload first.');
      return;
    }

    setUploading(true);
    setUploadModalError('');
    setShowUserBreakSuccess(false);
    setShowYouTubeSuccess(false);

    try {
      const student = students.find((s) => s.id === selectedStudent);
      const breakId = resolveBreakId(selectedBreakForUpload);
      const { titleSuffix } = await fetchExistingUserBreakMeta(
        selectedStudent,
        breakId
      );
      const youtubeTitle =
        formatYouTubeTitle(selectedBreakForUpload, student) + titleSuffix;

      const uploadResult = await uploadYoutubeBinaryFromBase64({
        fileBase64: pickedBase64Ref.current,
        title: youtubeTitle,
        description: `Break: ${selectedBreakForUpload.break_name}`,
        privacyStatus: 'unlisted',
        isShort: true,
      });

      let completedDate = new Date().toISOString().split('T')[0];
      if (uploadResult.uploadDate) {
        completedDate = uploadResult.uploadDate;
      }

      await persistUserBreak({
        studentId: selectedStudent,
        breakId,
        videoLink: uploadResult.youtubeUrl,
        completedDate,
      });

      setShowYouTubeSuccess(true);
      setYoutubeUrl(uploadResult.youtubeUrl);
      setShowUserBreakSuccess(true);
      setTimeout(() => {
        closeUploadModal();
        refreshBreaksList();
      }, 3000);
    } catch (e) {
      const msg = e?.message || 'Upload failed';
      if (isYoutubeReconnectError(msg)) {
        youtubeOAuthRef.current?.markNeedsReconnect?.(msg);
        setUploadModalError(msg);
      } else {
        alert(msg);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleUploadModalSave = () => {
    if (uploadModalTab === 'file') {
      void handleFileUploadSave();
    } else {
      void handleUrlUploadSave();
    }
  };

  const getSelectedStudentName = () => {
    if (!selectedStudent) return 'Select Student...';
    const student = students.find(s => s.id === selectedStudent);
    return student?.student_name || student?.username || 'Selected student';
  };

  const styles = getStyles(theme);

  const renderBreakFilters = () => (
    <View
      style={styles.filtersRow}
      {...(Platform.OS === 'web'
        ? { dataSet: { breaksBuilderFilters: 'true' } }
        : {})}
    >
      <BreaksBuilderFilterDropdown
        styles={styles}
        label="Weapon"
        selectedValue={selectedWeapon}
        options={availableWeapons}
        open={showWeaponDropdown}
        onOpenChange={(next) => {
          setShowWeaponDropdown(next);
          if (next) {
            setShowMaterialDropdown(false);
            setShowQuantityDropdown(false);
          }
        }}
        onSelect={setSelectedWeapon}
      />
      <BreaksBuilderFilterDropdown
        styles={styles}
        label="Material"
        selectedValue={selectedMaterial}
        options={availableMaterials}
        open={showMaterialDropdown}
        onOpenChange={(next) => {
          setShowMaterialDropdown(next);
          if (next) {
            setShowWeaponDropdown(false);
            setShowQuantityDropdown(false);
          }
        }}
        onSelect={setSelectedMaterial}
      />
      <BreaksBuilderFilterDropdown
        styles={styles}
        label="Quantity"
        selectedValue={selectedQuantity}
        options={availableQuantities}
        open={showQuantityDropdown}
        onOpenChange={(next) => {
          setShowQuantityDropdown(next);
          if (next) {
            setShowWeaponDropdown(false);
            setShowMaterialDropdown(false);
          }
        }}
        onSelect={setSelectedQuantity}
      />
    </View>
  );

  return (
    <View
      style={styles.container}
      {...(Platform.OS === 'web'
        ? {
            dataSet: { screen: 'breaks-builder' },
            dataTheme: theme.isDarkMode ? 'dark' : 'light',
          }
        : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      
      {/* Header */}
      <View style={styles.header}>
        <View style={styles.hamburgerContainer}>
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <View style={styles.breadcrumbContainer}>
          <Text style={styles.breadcrumb}>Upload Existing Breaks</Text>
        </View>
        <View style={styles.profileIconContainer}>
          <ProfileIcon navigation={navigation} user={currentUser} />
        </View>
      </View>

      {/* Tabs Container */}
      <View style={styles.tabsWrapper}>
        <View style={styles.tabsContainer}>
          {TABS.map((tab) => (
            <TouchableOpacity
              key={tab.id}
              style={[
                styles.tab,
                activeTab === tab.id && styles.tabActive,
              ]}
              onPress={() => setActiveTab(tab.id)}
              activeOpacity={0.7}
            >
              <Text
                style={[
                  styles.tabText,
                  activeTab === tab.id && styles.tabTextActive,
                ]}
              >
                {tab.label}
              </Text>
              {activeTab === tab.id && (
                <View style={styles.tabIndicator} />
              )}
            </TouchableOpacity>
          ))}
        </View>
      </View>

      {/* Tab Content */}
      <ScrollView style={styles.content} contentContainerStyle={styles.contentContainer}>
        {activeTab === 'course' && (
          <View style={styles.tabContent}>
            {/* Dropdowns Row */}
            <View style={styles.dropdownsRow}>
              {/* Select Course */}
              <View style={[styles.formField, showCourseDropdown && styles.formFieldDropdownOpen]}>
                <Text style={styles.label}>Select Course</Text>
                <TouchableOpacity
                  style={styles.dropdownButton}
                  onPress={() => setShowCourseDropdown(!showCourseDropdown)}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                >
                  <Text
                    style={[
                      styles.dropdownButtonText,
                      !selectedCourse && styles.dropdownButtonTextPlaceholder,
                    ]}
                    numberOfLines={1}
                  >
                    {selectedCourse ? selectedCourse.course_name : 'Select Course...'}
                  </Text>
                  <Text style={styles.dropdownArrow}>
                    {showCourseDropdown ? '▲' : '▼'}
                  </Text>
                </TouchableOpacity>

                {showCourseDropdown && (
                  <>
                    {Platform.OS === 'web' && (
                      <TouchableOpacity
                        style={styles.dropdownOverlay}
                        activeOpacity={1}
                        onPress={() => setShowCourseDropdown(false)}
                      />
                    )}
                    <View
                      style={styles.dropdownMenu}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                    >
                      <ScrollView style={styles.dropdownScrollView}>
                        {loadingCourses ? (
                          <Text style={styles.loadingText}>Loading courses...</Text>
                        ) : courses.length === 0 ? (
                          <Text style={styles.emptyText}>No courses found</Text>
                        ) : (
                          courses.map((course) => (
                            <TouchableOpacity
                              key={course.id}
                              style={[
                                styles.dropdownOption,
                                selectedCourse?.id === course.id && styles.dropdownOptionSelected,
                              ]}
                              onPress={() => {
                                setSelectedCourse(course);
                                setShowCourseDropdown(false);
                              }}
                              {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                            >
                              <Text
                                style={[
                                  styles.dropdownOptionText,
                                  selectedCourse?.id === course.id && styles.dropdownOptionTextSelected,
                                ]}
                              >
                                {course.course_name}
                              </Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </ScrollView>
                    </View>
                  </>
                )}
              </View>

              {/* Select Student */}
              <View style={[styles.formField, showStudentDropdown && styles.formFieldDropdownOpen]}>
                <Text style={styles.label}>Select Student</Text>
                <TouchableOpacity
                  style={styles.dropdownButton}
                  onPress={() => setShowStudentDropdown(!showStudentDropdown)}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                >
                  <Text
                    style={[
                      styles.dropdownButtonText,
                      !selectedStudent && styles.dropdownButtonTextPlaceholder,
                    ]}
                    numberOfLines={1}
                  >
                    {getSelectedStudentName()}
                  </Text>
                  <Text style={styles.dropdownArrow}>
                    {showStudentDropdown ? '▲' : '▼'}
                  </Text>
                </TouchableOpacity>

                {showStudentDropdown && (
                  <>
                    {Platform.OS === 'web' && (
                      <TouchableOpacity
                        style={styles.dropdownOverlay}
                        activeOpacity={1}
                        onPress={() => setShowStudentDropdown(false)}
                      />
                    )}
                    <View
                      style={styles.dropdownMenu}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                    >
                      <ScrollView style={styles.dropdownScrollView}>
                        {loadingStudents ? (
                          <Text style={styles.loadingText}>Loading students...</Text>
                        ) : students.length === 0 ? (
                          <Text style={styles.emptyText}>No students found</Text>
                        ) : (
                          students.map((student) => (
                            <TouchableOpacity
                              key={student.id}
                              style={[
                                styles.dropdownOption,
                                selectedStudent === student.id && styles.dropdownOptionSelected,
                              ]}
                              onPress={() => {
                                setSelectedStudent(student.id);
                                setShowStudentDropdown(false);
                              }}
                              {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                            >
                              <Text
                                style={[
                                  styles.dropdownOptionText,
                                  selectedStudent === student.id && styles.dropdownOptionTextSelected,
                                ]}
                              >
                                {student.student_name || student.username}
                                {student.student_grade && ` - ${student.student_grade}`}
                              </Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </ScrollView>
                    </View>
                  </>
                )}
              </View>
            </View>

            {selectedCourse && renderBreakFilters()}

            {/* Breaks Table */}
            {selectedCourse && (
              <View style={styles.tableContainer}>
                {loadingBreaks ? (
                  <Text style={styles.loadingText}>Loading breaks...</Text>
                ) : breaks.length === 0 ? (
                  <Text style={styles.emptyText}>No breaks found</Text>
                ) : (
                  <>
                    <BreaksBuilderTable
                      breaks={breaks}
                      styles={styles}
                      isDarkMode={theme.isDarkMode}
                      onPressRow={handleBreakClick}
                      onPressCompletedVideo={openCompletedVideo}
                    />
                  </>
                )}
              </View>
            )}
          </View>
        )}

        {activeTab === 'grade' && (
          <View style={styles.tabContent}>
            {/* Dropdowns Row */}
            <View style={styles.dropdownsRow}>
              {/* Select Grade */}
              <View style={[styles.formField, showGradeDropdown && styles.formFieldDropdownOpen]}>
                <Text style={styles.label}>Select Grade</Text>
                <TouchableOpacity
                  style={styles.dropdownButton}
                  onPress={() => setShowGradeDropdown(!showGradeDropdown)}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                >
                  <Text
                    style={[
                      styles.dropdownButtonText,
                      !selectedGrade && styles.dropdownButtonTextPlaceholder,
                    ]}
                    numberOfLines={1}
                  >
                    {selectedGrade ? selectedGrade.grade_name : 'Select Grade...'}
                  </Text>
                  <Text style={styles.dropdownArrow}>
                    {showGradeDropdown ? '▲' : '▼'}
                  </Text>
                </TouchableOpacity>

                {showGradeDropdown && (
                  <>
                    {Platform.OS === 'web' && (
                      <TouchableOpacity
                        style={styles.dropdownOverlay}
                        activeOpacity={1}
                        onPress={() => setShowGradeDropdown(false)}
                      />
                    )}
                    <View
                      style={styles.dropdownMenu}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                    >
                      <ScrollView style={styles.dropdownScrollView}>
                        {loadingGrades ? (
                          <Text style={styles.loadingText}>Loading grades...</Text>
                        ) : grades.length === 0 ? (
                          <Text style={styles.emptyText}>No grades found</Text>
                        ) : (
                          grades.map((grade) => (
                            <TouchableOpacity
                              key={grade.id}
                              style={[
                                styles.dropdownOption,
                                selectedGrade?.id === grade.id && styles.dropdownOptionSelected,
                              ]}
                              onPress={() => {
                                setSelectedGrade(grade);
                                setShowGradeDropdown(false);
                              }}
                              {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                            >
                              <Text
                                style={[
                                  styles.dropdownOptionText,
                                  selectedGrade?.id === grade.id && styles.dropdownOptionTextSelected,
                                ]}
                              >
                                {grade.grade_name}
                              </Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </ScrollView>
                    </View>
                  </>
                )}
              </View>

              {/* Select Student */}
              <View style={[styles.formField, showStudentDropdown && styles.formFieldDropdownOpen]}>
                <Text style={styles.label}>Select Student</Text>
                <TouchableOpacity
                  style={styles.dropdownButton}
                  onPress={() => setShowStudentDropdown(!showStudentDropdown)}
                  {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
                >
                  <Text
                    style={[
                      styles.dropdownButtonText,
                      !selectedStudent && styles.dropdownButtonTextPlaceholder,
                    ]}
                    numberOfLines={1}
                  >
                    {getSelectedStudentName()}
                  </Text>
                  <Text style={styles.dropdownArrow}>
                    {showStudentDropdown ? '▲' : '▼'}
                  </Text>
                </TouchableOpacity>

                {showStudentDropdown && (
                  <>
                    {Platform.OS === 'web' && (
                      <TouchableOpacity
                        style={styles.dropdownOverlay}
                        activeOpacity={1}
                        onPress={() => setShowStudentDropdown(false)}
                      />
                    )}
                    <View
                      style={styles.dropdownMenu}
                      {...(Platform.OS === 'web' ? { dataSet: { dropdownSurface: 'true' } } : {})}
                    >
                      <ScrollView style={styles.dropdownScrollView}>
                        {loadingStudents ? (
                          <Text style={styles.loadingText}>Loading students...</Text>
                        ) : students.length === 0 ? (
                          <Text style={styles.emptyText}>No students found</Text>
                        ) : (
                          students.map((student) => (
                            <TouchableOpacity
                              key={student.id}
                              style={[
                                styles.dropdownOption,
                                selectedStudent === student.id && styles.dropdownOptionSelected,
                              ]}
                              onPress={() => {
                                setSelectedStudent(student.id);
                                setShowStudentDropdown(false);
                              }}
                              {...(Platform.OS === 'web' ? { dataSet: { dropdownRow: 'true' } } : {})}
                            >
                              <Text
                                style={[
                                  styles.dropdownOptionText,
                                  selectedStudent === student.id && styles.dropdownOptionTextSelected,
                                ]}
                              >
                                {student.student_name || student.username}
                                {student.student_grade && ` - ${student.student_grade}`}
                              </Text>
                            </TouchableOpacity>
                          ))
                        )}
                      </ScrollView>
                    </View>
                  </>
                )}
              </View>
            </View>

            {selectedGrade && renderBreakFilters()}

            {/* Breaks Table */}
            {selectedGrade && (
              <View style={styles.tableContainer}>
                {loadingBreaks ? (
                  <Text style={styles.loadingText}>Loading breaks...</Text>
                ) : breaks.length === 0 ? (
                  <Text style={styles.emptyText}>No breaks found</Text>
                ) : (
                  <>
                    <BreaksBuilderTable
                      breaks={breaks}
                      styles={styles}
                      isDarkMode={theme.isDarkMode}
                      onPressRow={handleBreakClick}
                      onPressCompletedVideo={openCompletedVideo}
                    />
                  </>
                )}
              </View>
            )}
          </View>
        )}
      </ScrollView>

      {/* Upload Break Modal */}
      <Modal
        visible={showUploadModal}
        transparent
        animationType="fade"
        onRequestClose={closeUploadModal}
      >
        <View style={styles.uploadModalRoot}>
          <Pressable
            style={styles.uploadModalBackdrop}
            onPress={() => {
              if (pickingFileRef.current) return;
              closeUploadModal();
            }}
          />
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>Upload Break</Text>
            {selectedBreakForUpload?.break_name ? (
              <Text style={styles.modalBreakHint}>{selectedBreakForUpload.break_name}</Text>
            ) : null}

            <View style={styles.uploadModalTabs}>
              {UPLOAD_MODAL_TABS.map((t) => {
                const active = uploadModalTab === t.id;
                return (
                  <TouchableOpacity
                    key={t.id}
                    style={[styles.uploadModalTab, active && styles.uploadModalTabActive]}
                    onPress={() => {
                      setUploadModalTab(t.id);
                      setUploadModalError('');
                    }}
                    activeOpacity={0.85}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: active }}
                  >
                    <Text
                      style={[
                        styles.uploadModalTabLabel,
                        active && styles.uploadModalTabLabelActive,
                      ]}
                    >
                      {t.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            {uploadModalTab === 'url' ? (
              <View style={styles.modalField}>
                <Text style={styles.modalLabel}>URL</Text>
                <TextInput
                  style={styles.modalInput}
                  value={uploadUrl}
                  onChangeText={setUploadUrl}
                  placeholder="Paste from YouTube, Facebook, etc to save on the Ming Ch'uan YouTube Channel and display on the app."
                  placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                  autoCapitalize="none"
                  autoCorrect={false}
                  multiline
                  numberOfLines={3}
                />
              </View>
            ) : (
              <>
                <YoutubeOAuthBar ref={youtubeOAuthRef} variant="upload-break-modal" />
                {Platform.OS === 'web' &&
                  React.createElement('input', {
                    ref: (el) => {
                      webFileInputRef.current = el;
                    },
                    type: 'file',
                    accept: 'video/*',
                    style: { display: 'none' },
                    onChange: onWebFileChange,
                    onClick: (ev) => ev.stopPropagation(),
                  })}
                <TouchableOpacity
                  style={styles.filePickButton}
                  onPress={(e) => {
                    if (Platform.OS === 'web' && e?.stopPropagation) e.stopPropagation();
                    void launchFilePick();
                  }}
                  disabled={uploading}
                  activeOpacity={0.85}
                >
                  <Text style={styles.filePickButtonText}>Upload</Text>
                </TouchableOpacity>
                <TextInput
                  style={styles.readOnlyFileInput}
                  value={pickedFileName}
                  editable={false}
                  placeholder="No file selected"
                  placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                />
              </>
            )}

            {uploadModalError ? (
              <View style={[styles.uploadModalErrorBox]}>
                <Text style={styles.uploadModalErrorText}>{uploadModalError}</Text>
              </View>
            ) : null}

            {showUserBreakSuccess ? (
              <View style={styles.successMessageBox}>
                <Text style={styles.successMessageText}>Your break has been saved.</Text>
              </View>
            ) : null}

            {showYouTubeSuccess ? (
              <View style={styles.successMessageBox}>
                <Text style={styles.successMessageText}>
                  Your YouTube video has been successfully uploaded.
                </Text>
              </View>
            ) : null}

            {youtubeUrl && showYouTubeSuccess ? (
              <TouchableOpacity
                style={styles.youtubeLinkButton}
                onPress={() => {
                  if (Platform.OS === 'web') {
                    window.open(youtubeUrl, '_blank');
                  } else {
                    Linking.openURL(youtubeUrl).catch(() => {
                      alert('Could not open YouTube URL');
                    });
                  }
                }}
              >
                <Text style={styles.youtubeLinkButtonText}>Link to YouTube Video</Text>
              </TouchableOpacity>
            ) : null}

            <View style={styles.modalButtons}>
              <TouchableOpacity
                style={[styles.modalButton, styles.modalButtonCancel]}
                onPress={closeUploadModal}
                disabled={uploading}
              >
                <Text style={styles.modalButtonCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[
                  styles.modalButton,
                  styles.modalButtonSave,
                  uploading && styles.modalButtonDisabled,
                ]}
                onPress={handleUploadModalSave}
                disabled={uploading}
              >
                {uploading ? (
                  <ActivityIndicator color="#000000" />
                ) : (
                  <Text style={styles.modalButtonSaveText}>
                    {uploadModalTab === 'file' ? 'Submit' : 'Save'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      <UserBreakVideosModal
        visible={completedVideoBreak != null}
        onClose={closeCompletedVideo}
        title={completedVideoBreak?.break_name || 'Break video'}
        videoLinks={completedVideoBreak?.video_link}
        overlayStyle={styles.videoModalOverlay}
        contentStyle={styles.videoModalContent}
      />

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

const getStyles = (theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    borderBottomWidth: 0,
  },
  hamburgerContainer: {
    marginRight: 15,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  breadcrumbContainer: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  breadcrumb: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
    letterSpacing: 0.5,
    backgroundColor: 'transparent',
  },
  profileIconContainer: {
    backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
  },
  tabsWrapper: {
    width: '100%',
    alignItems: 'center',
    paddingVertical: theme.isDarkMode ? 15 : 10,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  tabsContainer: {
    width: '90%',
    flexDirection: 'row',
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: theme.isDarkMode ? 8 : 8,
    padding: theme.isDarkMode ? 4 : 0,
    borderWidth: theme.isDarkMode ? 0 : 1,
    borderColor: theme.isDarkMode ? 'transparent' : '#E0E0E0',
    borderBottomWidth: theme.isDarkMode ? 0 : 2,
    borderBottomColor: theme.isDarkMode ? 'transparent' : '#E0E0E0',
  },
  tab: {
    flex: 1,
    paddingVertical: theme.isDarkMode ? 12 : 12,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    borderRadius: theme.isDarkMode ? 6 : 0,
    backgroundColor: theme.isDarkMode ? theme.colors.background : 'transparent',
    marginHorizontal: theme.isDarkMode ? 2 : 0,
  },
  tabActive: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : 'transparent',
  },
  tabText: {
    fontSize: 16,
    fontWeight: '500',
    color: theme.isDarkMode ? theme.colors.text : '#666666',
  },
  tabTextActive: {
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
  },
  tabIndicator: {
    position: 'absolute',
    bottom: theme.isDarkMode ? -4 : -2,
    left: theme.isDarkMode ? '10%' : 0,
    right: theme.isDarkMode ? '10%' : 0,
    height: theme.isDarkMode ? 3 : 3,
    backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    borderRadius: theme.isDarkMode ? 2 : 0,
    width: '80%',
    alignSelf: 'center',
  },
  content: {
    flex: 1,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    ...(Platform.OS === 'web' ? { overflow: 'visible' } : {}),
  },
  contentContainer: {
    width: '90%',
    alignSelf: 'center',
    padding: 20,
    paddingBottom: 40,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: theme.isDarkMode ? 8 : 0,
    marginTop: theme.isDarkMode ? 10 : 0,
    ...(Platform.OS === 'web' ? { overflow: 'visible' } : {}),
  },
  tabContent: {
    flex: 1,
    minHeight: 200,
    ...(Platform.OS === 'web' ? { overflow: 'visible' } : {}),
  },
  tabContentText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    textAlign: 'center',
    marginTop: 20,
  },
  dropdownsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    gap: 15,
    zIndex: 2000,
    ...(Platform.OS === 'web' ? {
      overflow: 'visible',
    } : {}),
  },
  formField: {
    flex: 1,
    marginBottom: 20,
    position: 'relative',
    zIndex: 1000,
    elevation: 10,
    ...(Platform.OS === 'web' ? {
      position: 'relative',
      overflow: 'visible',
    } : {}),
  },
  formFieldDropdownOpen: {
    zIndex: 50000,
    elevation: 50,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
    marginBottom: 8,
    backgroundColor: theme.isDarkMode ? 'transparent' : 'transparent',
  },
  dropdownButton: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.isDarkMode ? '#000000' : '#FFFFFF',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    borderRadius: 8,
    padding: 15,
    minHeight: 50,
  },
  dropdownButtonText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdownButtonTextPlaceholder: {
    color: theme.isDarkMode ? '#888' : '#999',
    backgroundColor: 'transparent',
  },
  dropdownArrow: {
    fontSize: 12,
    color: theme.isDarkMode ? theme.colors.text : '#666',
    marginLeft: 10,
    backgroundColor: 'transparent',
  },
  dropdownOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 9998,
    ...(Platform.OS !== 'web' ? {
      position: 'absolute',
    } : {}),
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    borderRadius: 8,
    maxHeight: 300,
    zIndex: 99999,
    elevation: 60,
    ...(Platform.OS === 'web' ? {
      position: 'absolute',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
      overflow: 'visible',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.5,
      shadowRadius: 8,
      elevation: 10,
    }),
  },
  dropdownScrollView: {
    maxHeight: 300,
  },
  dropdownOption: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 15,
    borderBottomWidth: 1,
    borderBottomColor: theme.isDarkMode ? theme.colors.border : '#F0F0F0',
  },
  dropdownOptionSelected: {
    backgroundColor: theme.isDarkMode ? 'rgba(253, 253, 10, 0.1)' : 'rgba(212, 175, 55, 0.1)',
  },
  dropdownOptionText: {
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    flex: 1,
    backgroundColor: 'transparent',
  },
  dropdownOptionTextSelected: {
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
    backgroundColor: 'transparent',
  },
  filtersRow: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginBottom: 20,
    gap: 10,
    flexWrap: 'wrap',
    zIndex: 4000,
    ...(Platform.OS === 'web'
      ? { overflow: 'visible', position: 'relative' }
      : {}),
  },
  filterContainer: {
    position: 'relative',
    zIndex: 1000,
    ...(Platform.OS === 'web' ? {
      position: 'relative',
    } : {}),
  },
  filterButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#F5F5F5',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    minWidth: 120,
  },
  filterButtonActive: {
    backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    borderColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
  },
  filterButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    flex: 1,
    backgroundColor: 'transparent',
  },
  filterDropdownOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    zIndex: 9998,
    ...(Platform.OS !== 'web' ? {
      position: 'absolute',
    } : {}),
  },
  filterDropdown: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    borderRadius: 8,
    maxHeight: 200,
    zIndex: 9999,
    ...(Platform.OS === 'web' ? {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.5)',
    } : {
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 4 },
      shadowOpacity: 0.5,
      shadowRadius: 8,
      elevation: 10,
    }),
  },
  filterDropdownScroll: {
    maxHeight: 200,
  },
  filterOption: {
    paddingVertical: 10,
    paddingHorizontal: 12,
    borderBottomWidth: 1,
    borderBottomColor: theme.isDarkMode ? theme.colors.border : '#F0F0F0',
  },
  filterOptionSelected: {
    backgroundColor: theme.isDarkMode ? 'rgba(253, 253, 10, 0.1)' : 'rgba(212, 175, 55, 0.1)',
  },
  filterOptionText: {
    fontSize: 14,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    backgroundColor: 'transparent',
  },
  tableContainer: {
    width: '90%',
    alignSelf: 'center',
    marginTop: 20,
    zIndex: 1,
    elevation: 1,
  },
  syllabusTableCard: {
    borderRadius: 10,
    overflow: 'hidden',
    borderWidth: theme.isDarkMode ? 1 : StyleSheet.hairlineWidth,
    borderColor: theme.colors.border,
  },
  tableHorizontalScroll: {
    width: '100%',
    alignSelf: 'stretch',
    flexGrow: 0,
    ...(Platform.OS === 'web' && { overflowX: 'auto' }),
  },
  tableHorizontalScrollContent: {
    paddingBottom: 8,
  },
  tableInner: {
    flexDirection: 'column',
  },
  tableHeaderRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    paddingVertical: 10,
    paddingHorizontal: 8,
  },
  tableHeaderRowBgDark: {
    backgroundColor: '#1e1e1e',
    borderBottomWidth: 2,
    borderBottomColor: '#FDFD0A',
  },
  tableHeaderRowBgLight: {
    backgroundColor: '#ececec',
    borderBottomWidth: 2,
    borderBottomColor: '#c9a227',
  },
  tableHeaderCell: {
    fontSize: 11,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    paddingRight: 6,
    minWidth: 0,
  },
  tableHeaderTextDark: { color: '#FDFD0A' },
  tableHeaderTextLight: { color: '#8b6914' },
  tableDataRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderWidth: 0,
    minHeight: 48,
  },
  tableRowBase: {
    backgroundColor: theme.isDarkMode ? '#252525' : '#FFFFFF',
  },
  tableRowAlt: {
    backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#E8E8E8',
  },
  tableRowPressed: {
    opacity: 0.88,
  },
  tableCellWrap: {
    justifyContent: 'center',
    paddingRight: 6,
    minWidth: 0,
    backgroundColor: 'transparent',
  },
  completedCell: {
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 40,
    backgroundColor: 'transparent',
  },
  completedCellButton: {
    backgroundColor: 'transparent',
  },
  tableCellText: {
    fontSize: 13,
    lineHeight: 18,
    paddingRight: 4,
    minWidth: 0,
    backgroundColor: 'transparent',
  },
  tableCellTextDark: { color: '#FFFFFF' },
  tableCellTextLight: { color: '#0d0d0d' },
  videoModalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.65)',
    justifyContent: 'center',
    padding: 20,
  },
  videoModalContent: {
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    maxWidth: 720,
    width: '100%',
    alignSelf: 'center',
  },
  videoModalClose: {
    alignSelf: 'flex-end',
    padding: 8,
  },
  videoModalCloseText: {
    fontSize: 22,
    color: theme.colors.text,
    fontWeight: '600',
  },
  videoModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.text,
    marginBottom: 12,
  },
  youtubeContainer: {
    width: '100%',
    aspectRatio: 16 / 9,
    backgroundColor: '#000',
    borderRadius: 8,
    overflow: 'hidden',
  },
  youtubeIframe: {
    width: '100%',
    height: '100%',
    border: 'none',
  },
  youtubeWebView: {
    flex: 1,
    width: '100%',
    minHeight: 220,
  },
  loadingText: {
    padding: 15,
    textAlign: 'center',
    color: theme.isDarkMode ? theme.colors.text : '#666',
    fontSize: 14,
  },
  emptyText: {
    padding: 15,
    textAlign: 'center',
    color: theme.isDarkMode ? theme.colors.text : '#666',
    fontSize: 14,
    fontStyle: 'italic',
  },
  uploadModalRoot: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  uploadModalBackdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
  },
  modalContent: {
    width: width * 0.9,
    maxWidth: 500,
    backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    zIndex: 2,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }
      : { elevation: 8 }),
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: theme.isDarkMode ? theme.colors.heading : '#000000',
    marginBottom: 8,
    textAlign: 'center',
  },
  modalBreakHint: {
    fontSize: 14,
    color: theme.colors.text,
    opacity: 0.85,
    textAlign: 'center',
    marginBottom: 14,
  },
  uploadModalTabs: {
    flexDirection: 'row',
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    marginBottom: 16,
  },
  uploadModalTab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  uploadModalTabActive: {
    borderBottomColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
  },
  uploadModalTabLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.text,
    opacity: 0.75,
  },
  uploadModalTabLabelActive: {
    color: theme.isDarkMode ? theme.colors.heading : '#8b6914',
    opacity: 1,
  },
  filePickButton: {
    backgroundColor: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    padding: 14,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  filePickButtonText: {
    color: theme.isDarkMode ? '#000000' : '#000000',
    fontWeight: '700',
    fontSize: 16,
  },
  readOnlyFileInput: {
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    color: theme.colors.text,
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FAFAFA',
  },
  uploadModalErrorBox: {
    backgroundColor: '#c62828',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
  },
  uploadModalErrorText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
  },
  modalField: {
    marginBottom: 20,
  },
  modalLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    marginBottom: 8,
  },
  modalInput: {
    borderWidth: 1,
    borderColor: theme.isDarkMode ? theme.colors.border : '#E0E0E0',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    color: theme.isDarkMode ? theme.colors.text : '#000000',
    backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
  },
  modalButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 10,
  },
  modalButton: {
    flex: 1,
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  modalButtonCancel: {
    backgroundColor: '#999999', // Grey background
    borderWidth: 1,
    borderColor: '#999999',
  },
  modalButtonSave: {
    backgroundColor: '#d4af37', // Yellow background
  },
  modalButtonDisabled: {
    opacity: 0.5,
  },
  modalButtonCancelText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  modalButtonSaveText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#000000', // Black text on yellow
  },
  successMessageBox: {
    backgroundColor: '#4CAF50', // Green background
    padding: 12,
    borderRadius: 8,
    marginBottom: 15,
    marginTop: 10,
  },
  youtubeLinkButton: {
    backgroundColor: '#FFD700', // Yellow background
    padding: 12,
    borderRadius: 8,
    marginTop: 10,
    marginBottom: 10,
    alignItems: 'center',
  },
  youtubeLinkButtonText: {
    color: '#000000', // Black text
    fontSize: 16,
    fontWeight: '600',
  },
  successMessageText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
  },
});
